import { useMemo, useState } from 'react';
import type { CodeLang, Env, RequestSpec } from '@/types';
import { CODE_LANGS, generateCode } from '@/codegen';

interface Props {
  env: Env;
  spec: RequestSpec;
  hasAuth: boolean;
  apiBaseOverride?: string;
}

export function CodeTabs({ env, spec, hasAuth, apiBaseOverride }: Props) {
  const [active, setActive] = useState<CodeLang>('curl');
  const [copied, setCopied] = useState(false);

  const code = useMemo(
    () => generateCode(active, env, spec, hasAuth, apiBaseOverride),
    [active, env, spec, hasAuth, apiBaseOverride]
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      className="lp-surface-dark lp-overflow-hidden lp-shadow-card"
      style={{ borderRadius: 22 }}
    >
      {/* Tab bar — full width. Copy moved into the code pane as an overlay so
       *  even narrow code columns can show all language labels in full. */}
      <div
        className="lp-flex lp-overflow-x-auto lp-items-center lp-gap-1 lp-px-2 lp-py-1.5"
        role="tablist"
        aria-label="Code language"
        style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
      >
        {CODE_LANGS.map((l) => {
          const isActive = active === l.id;
          return (
            <button
              key={l.id}
              onClick={() => setActive(l.id)}
              role="tab"
              aria-selected={isActive}
              className="lp-relative lp-whitespace-nowrap lp-rounded-lg lp-transition-colors lp-shrink-0"
              style={{
                padding: '7px 12px',
                fontSize: 13,
                lineHeight: '18px',
                fontWeight: isActive ? 600 : 500,
                letterSpacing: 0.1,
                color: isActive ? '#ffffff' : '#cbd5e1',
                background: isActive ? 'rgba(124, 58, 237, 0.22)' : 'transparent',
                border: isActive
                  ? '1px solid rgba(167, 139, 250, 0.45)'
                  : '1px solid transparent',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.color = '#ffffff';
                  e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.color = '#cbd5e1';
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              {l.label}
            </button>
          );
        })}
      </div>

      <div className="lp-relative">
        <pre
          className="lp-code-pane lp-text-[12.5px] lp-leading-relaxed lp-font-mono lp-p-4 lp-pr-20 lp-overflow-auto lp-max-h-[440px]"
          style={{ color: '#e2e8f0' }}
        >
          {code}
        </pre>
        <button
          onClick={handleCopy}
          aria-label="Copy code"
          className="lp-absolute lp-top-2.5 lp-right-2.5 lp-text-[11px] lp-font-medium lp-px-2.5 lp-py-1 lp-rounded-md lp-transition-colors lp-flex lp-items-center lp-gap-1.5"
          style={{
            background: copied ? 'rgba(124, 58, 237, 0.32)' : 'rgba(255,255,255,0.08)',
            color: '#e2e8f0',
            backdropFilter: 'blur(6px)',
          }}
          onMouseEnter={(e) => {
            if (!copied) e.currentTarget.style.background = 'rgba(255,255,255,0.16)';
          }}
          onMouseLeave={(e) => {
            if (!copied) e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
          }}
        >
          {copied ? (
            <>
              <svg className="lp-w-3 lp-h-3" style={{ color: '#a99dff' }} fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              Copied
            </>
          ) : (
            <>
              <svg className="lp-w-3 lp-h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <rect x="9" y="9" width="11" height="11" rx="2" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15V5a2 2 0 0 1 2-2h10" />
              </svg>
              Copy
            </>
          )}
        </button>
      </div>
    </div>
  );
}
